/**
 * Phase 10 Plan 01 — FOUND-06: DB-backed feature flags with React Query.
 *
 * Usage:
 *   const billingV2 = useFeatureFlag('release.billing_v2');
 *   if (billingV2) { ... }
 *
 * Design notes:
 *   (a) Single bulk query, per-key selectors. 60s staleTime + 60s refetchInterval
 *       satisfies ROADMAP Phase 10 success criterion #1 ("admin toggle propagates
 *       to client within 60s"). Cache key is a single ['feature_flags'] entry so
 *       the admin UI only needs one invalidateQueries call.
 *   (b) Fail mode is ALWAYS `false` regardless of namespace prefix. The prefix
 *       exists for human clarity in flag names and future percentage-rollout
 *       features. See RESEARCH §4 "Assumption A6": a `kill.*` flag meaning
 *       "kill switch engaged" also defaults to `false` on fetch failure, which
 *       correctly leaves the feature on (fail-open for the feature, fail-closed
 *       for the kill bit).
 *   (c) Flag keys MUST match /^(release|experiment|kill)\.[a-z0-9_]+$/ — the
 *       CHECK constraint on `feature_flags.key` enforces this at the DB layer.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type FlagRow = { key: string; enabled: boolean };

const STALE_TIME_MS = 60_000;
const REFETCH_INTERVAL_MS = 60_000;

/**
 * On fetch failure or missing key we fail closed to `false`. See header note (b).
 */
function failMode(_key: string): boolean {
  return false;
}

export function useFeatureFlags(): UseQueryResult<Map<string, boolean>, Error> {
  return useQuery<Map<string, boolean>, Error>({
    queryKey: ['feature_flags'],
    queryFn: async () => {
      // Cast: `feature_flags` is added by migration 20260415000001 but the
      // generated Database types haven't been regenerated yet. Safe — the
      // CHECK + RLS policies fully constrain the row shape.
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => Promise<{ data: FlagRow[] | null; error: Error | null }>;
        };
      })
        .from('feature_flags')
        .select('key, enabled');
      if (error) throw error;
      return new Map((data as FlagRow[]).map((r) => [r.key, r.enabled]));
    },
    staleTime: STALE_TIME_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

export function useFeatureFlag(key: string): boolean {
  const { data, isError } = useFeatureFlags();
  if (isError || !data) return failMode(key);
  const v = data.get(key);
  return v === undefined ? failMode(key) : v;
}
